// Copyright (c) Bentley Systems
import {
  Button,
  ExpandableBlock,
  LabeledSelect,
  ProgressRadial,
  Text,
  Tooltip,
} from "@itwin/itwinui-react";
import { useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { selectAccessToken } from "../app/authorization-slice";
import {
  selectMainMenuHeightWithMargin,
  selectSideMenuWidthWithMargin,
} from "../app/header-slice";
import { DawComponent } from "../Helpers/daw-component";
import { useNodePermission } from "../Helpers/useNodePermission";
import { useElementHeights } from "../Helpers/useElementHeights";
import { useOnPremTelemetryOverviewApi } from "./useOnPremTelemetryOverviewApi";
import ProjectRunsManagementTable from "./ProjectRunsManagementTable";
import OnPremHealthStatusBadge from "./OnPremHealthStatusBadge";
import OnPremVersionBadge from "./OnPremVersionBadge";
import {
  ClientProjectRow,
  ClientTelemetryOverview,
  DAY_FILTER_OPTIONS,
  dayFilterLabel,
  DayFilterValue,
  ON_PREM_RUN_RETENTION_DAYS,
  toClientProjectRows,
} from "./Models/on-prem-telemetry-models";

const DAY_FILTER_SELECT_OPTIONS = DAY_FILTER_OPTIONS.map((value) => ({
  value: String(value),
  label: dayFilterLabel(value),
}));

const OnPremTelemetry = () => {
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector(selectAccessToken);
  const sideMenuWidth = useAppSelector(selectSideMenuWidthWithMargin);
  const mainMenuHeight = useAppSelector(selectMainMenuHeightWithMargin);

  const { isNodeAllowed, isLoading: isPermissionLoading } = useNodePermission(
    DawComponent.DAW_COMPONENT_ON_PREM_TELEMETRY,
  );

  const [dayFilter, setDayFilter] = useState<DayFilterValue>(7);

  const {
    clients,
    isLoading,
    isMutating,
    fetchOverview,
    setProjectsMuted,
    deleteProjectRuns,
  } = useOnPremTelemetryOverviewApi({
    accessToken,
    dispatch,
    isNodeAllowed,
    dayFilter,
  });

  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight] = useElementHeights(headerRef);

  const allHeadersHeight = mainMenuHeight + headerHeight;

  const clientBlocks = useMemo(
    () =>
      clients.map((client: ClientTelemetryOverview) => {
        // Only show projects that fall within the selected health window, so the visible rows
        // always match what's used to compute the client's health status below. Muted projects
        // remain visible as long as they're within the window, so they can still be unmuted.
        const projectRows: ClientProjectRow[] = toClientProjectRows(client.mqttClientName, client.projects, dayFilter);

        return (
          <ExpandableBlock.Wrapper key={client.mqttClientName}>
            <ExpandableBlock.Trigger>
              <ExpandableBlock.ExpandIcon />
              <ExpandableBlock.LabelArea>
                <ExpandableBlock.Title>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <Text>
                      <b>{client.mqttClientName}</b>
                    </Text>
                    <OnPremHealthStatusBadge projects={client.projects} dayFilter={dayFilter} />
                    <OnPremVersionBadge version={client.installedVersion} />
                  </div>
                </ExpandableBlock.Title>
                <ExpandableBlock.Caption>
                  <Tooltip content="When this on-prem client last returned a run result, in any iTwin.">
                    <span>
                      Last used: <b>{client.lastUsed ? new Date(client.lastUsed).toLocaleString() : "—"}</b>
                    </span>
                  </Tooltip>
                  {" · "}
                  {dayFilter !== 0 && (
                    <>
                      <Tooltip content="Projects whose latest run on this client falls in the selected window. Each project counts once, however many times it ran.">
                        <span>
                          Projects run in the {dayFilterLabel(dayFilter).toLowerCase()}: <b>{projectRows.length}</b>
                        </span>
                      </Tooltip>
                      {" · "}
                    </>
                  )}
                  <Tooltip content={`All projects with a recorded latest run on this client, regardless of the selected window. Records older than ${ON_PREM_RUN_RETENTION_DAYS} days are removed.`}>
                    <span>
                      Tracked projects: <b>{client.trackedProjectCount ?? client.projects.length}</b>
                    </span>
                  </Tooltip>
                </ExpandableBlock.Caption>
              </ExpandableBlock.LabelArea>
            </ExpandableBlock.Trigger>
            <ExpandableBlock.Content>
              <ProjectRunsManagementTable
                rows={projectRows}
                isMutating={isMutating}
                onSetMuted={setProjectsMuted}
                onDelete={deleteProjectRuns}
              />
            </ExpandableBlock.Content>
          </ExpandableBlock.Wrapper>
        );
      }),
    [clients, dayFilter, isMutating, setProjectsMuted, deleteProjectRuns],
  );

  if (isPermissionLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", paddingTop: "48px" }}>
        <ProgressRadial />
      </div>
    );
  }

  if (!isNodeAllowed) {
    return (
      <div style={{ padding: "24px" }}>
        <Text>You do not have permission to access On-Prem Telemetry.</Text>
      </div>
    );
  }

  return (
    <div style={{ height: "100%" }}>
      <div className="becs-infopanelbody-footer-line becs-all-margin" ref={headerRef}>
        <div className="rowWrap" style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Text variant="title">On-Prem Version &amp; Usage</Text>
          <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}>
            <LabeledSelect
              displayStyle="inline"
              label="Health window"
              options={DAY_FILTER_SELECT_OPTIONS}
              value={String(dayFilter)}
              onChange={(value: string) => setDayFilter(Number(value) as DayFilterValue)}
              style={{ width: "160px" }}
            />
            <Button
              styleType="default"
              size="small"
              onClick={() => { void fetchOverview(); }}
              disabled={isLoading}
            >
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="becs-ltr-margin">
        <div
          style={{
            height: `calc(100vh - ${allHeadersHeight}px)`,
            width: `calc(100vw - ${sideMenuWidth}px)`,
            background: "var(--iui-color-background)",
            overflowY: "auto",
          }}
        >
          {isLoading && clients.length === 0 ? (
            <div
              className="becs-ltr-margin"
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                paddingTop: "24px",
              }}
            >
              <ProgressRadial indeterminate />
            </div>
          ) : clients.length === 0 ? (
            <div className="becs-ltr-margin" style={{ paddingTop: "16px" }}>
              <Text>No on-prem telemetry data found.</Text>
            </div>
          ) : (
            clientBlocks
          )}
        </div>
      </div>
    </div>
  );
};

export default OnPremTelemetry;

